import { APIResult, Order } from "../my-type";
import { authUserData } from ".";
import {
  OrderService as GeneratedOrderService,
  type AssignOrderRequest,
  type RentalTimeUpdateRequest,
  type SetRelatedOrderRequest,
  type SignatureRequest,
  type TransferOrderRequest,
  type UpdateOrderRequest,
  callApi,
  configureApiClient,
  fetchWithApiClient,
} from "../api/client";

export interface FlightLatestInfo {
  scheduleArrivalTime?: string | null;
  estimatedArrivalTime?: string | null;
  arrivalRemark?: string | null;
  scheduleDepartureTime?: string | null;
  estimatedDepartureTime?: string | null;
  departureRemark?: string | null;
}

type ApiClientError = Error & { status?: number; body?: unknown };

const normalizeOrder = (order: any): Order => ({
  ...order,
  isBlindOrder: order?.isBlindOrder ?? false,
});

const normalizeOrders = (orders: any[]): Order[] => orders.map(normalizeOrder);

const configureOrderApiClient = () => {
  configureApiClient({
    tokenProvider: () => authUserData()?.jwtToken ?? null,
  });
};

const parseErrorResult = (error: unknown, fallbackMessage: string): APIResult => {
  const apiError = error as ApiClientError;
  const body = apiError?.body as
    | { status?: number; detail?: string; message?: string }
    | string
    | undefined;

  const statusFromBody =
    body && typeof body === "object" && typeof body.status === "number"
      ? body.status
      : undefined;
  const messageFromBody = typeof body === "string" ? body : body?.detail || body?.message;

  return {
    statusCode: apiError?.status ?? statusFromBody ?? 500,
    message: messageFromBody || apiError?.message || fallbackMessage,
    data: body ?? null,
  };
};

const runOrderMutation = async (
  action: () => Promise<unknown>,
  successMessage: string,
  fallbackErrorMessage: string
): Promise<APIResult> => {
  try {
    configureOrderApiClient();
    await callApi(action, fallbackErrorMessage);
    return {
      statusCode: 200,
      message: successMessage,
      data: null,
    };
  } catch (error) {
    console.log(error);
    return parseErrorResult(error, fallbackErrorMessage);
  }
};

export const createOrder = async (order: Order) => {
  try {
    configureOrderApiClient();
    return await callApi(
      () =>
        GeneratedOrderService.postApiOrderCreateAdminOrder({
          ...(order as unknown as import("../generated/api").Order),
          isBlindOrder: false,
        }),
      "建立訂單失敗"
    );
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getOrder = async (orderId: string) => {
  try {
    configureOrderApiClient();
    const data = await callApi(
      () => GeneratedOrderService.getApiOrder(orderId),
      "取得訂單失敗"
    );
    return normalizeOrder(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getFlightLatestInfo = async (
  orderId: string
): Promise<FlightLatestInfo | null> => {
  try {
    configureOrderApiClient();
    return await callApi(
      () => GeneratedOrderService.getApiOrderFlightLatestInfo(orderId),
      "取得航班最新資訊失敗"
    );
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getUserOrders = async () => {
  try {
    configureOrderApiClient();
    const data = await callApi(
      () => GeneratedOrderService.getApiOrderOrders(),
      "取得使用者訂單失敗"
    );
    return normalizeOrders(data as any[]);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getOrdersByTeamCode = async (
  teamCode: string,
  yearMonth: string
): Promise<Order[] | null> => {
  const yearMonthRegex = /^\d{4}-(0[1-9]|1[0-2])$/;

  if (!yearMonth) {
    const now = new Date();
    yearMonth = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, "0")}`;
  }

  if (!yearMonthRegex.test(yearMonth)) {
    console.error("Invalid yearMonth format. Expected yyyy-mm.");
    return null;
  }

  try {
    configureOrderApiClient();
    const data = await callApi(
      () => GeneratedOrderService.getApiOrderGetOrdersByTeamCode(teamCode, yearMonth),
      "取得車隊訂單失敗"
    );
    return normalizeOrders(data as any[]);
  } catch (error) {
    console.error("Error fetching orders by team code:", error);
    return null;
  }
};

export const getPendingOrders = async () => {
  try {
    configureOrderApiClient();
    const data = await callApi(
      () => GeneratedOrderService.getApiOrderPendingOrders(),
      "取得待接訂單失敗"
    );
    return normalizeOrders(data as any[]);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getTakenOrders = async () => {
  try {
    configureOrderApiClient();
    const data = await callApi(
      () => GeneratedOrderService.getApiOrderTakenOrders(),
      "取得已接訂單失敗"
    );
    return normalizeOrders(data as any[]);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const takeOrder = async (orderID: string) =>
  runOrderMutation(
    () => GeneratedOrderService.putApiOrderTakeOrder(orderID),
    "接單成功",
    "接單失敗"
  );

export const assignOrder = async (orderID: string, driverUserUID: number) => {
  const request: AssignOrderRequest = {
    driverUserUID,
  };

  return runOrderMutation(
    () => GeneratedOrderService.putApiOrderAssignOrder(orderID, request),
    "指派司機成功",
    "指派司機失敗"
  );
};

export const setRelatedOrder = async (orderID: string, relatedOrderID: string | null) => {
  const request: SetRelatedOrderRequest = {
    relatedOrderID,
  };

  return runOrderMutation(
    () => GeneratedOrderService.putApiOrderSetRelatedOrder(orderID, request),
    relatedOrderID ? "關聯訂單設定成功" : "關聯訂單已清除",
    relatedOrderID ? "關聯訂單設定失敗" : "關聯訂單清除失敗"
  );
};

export const cancelTakenOrder = async (orderID: string) =>
  runOrderMutation(
    () => GeneratedOrderService.putApiOrderCancelTakenOrder(orderID),
    "取消接單成功",
    "取消接單失敗"
  );

export const cancelOrder = async (orderID: string) =>
  runOrderMutation(
    () => GeneratedOrderService.putApiOrderCancelOrder(orderID),
    "訂單取消成功",
    "訂單取消失敗"
  );

export const rideOrder = async (orderID: string) =>
  runOrderMutation(
    () => GeneratedOrderService.putApiOrderRideOrder(orderID),
    "訂單狀態修改成功",
    "訂單狀態修改失敗"
  );

export const completeOrder = async (orderID: string) =>
  runOrderMutation(
    () => GeneratedOrderService.putApiOrderCompleteOrder(orderID),
    "訂單狀態修改成功",
    "訂單狀態修改失敗"
  );

export const saveSignature = async (orderID: string, imageDataUrl: string): Promise<boolean> => {
  try {
    configureOrderApiClient();
    const request: SignatureRequest = {
      image: imageDataUrl,
    };
    await callApi(
      () => GeneratedOrderService.postApiOrderSaveSignature(orderID, request),
      "儲存簽名失敗"
    );
    return true;
  } catch (error) {
    console.error("Error saving signature:", error);
    return false;
  }
};

export const getSignature = async (orderId: string): Promise<Blob | null> => {
  try {
    configureOrderApiClient();
    const response = await fetchWithApiClient(`/api/Order/${orderId}/ViewRentForm`, {
      method: "GET",
    });

    if (!response.ok) {
      console.error("Error fetching signature:", response.status);
      return null;
    }

    return await response.blob();
  } catch (error) {
    console.error("Error fetching signature:", error);
    return null;
  }
};

export const updateRentalTimes = async (
  orderID: string,
  rentalStartTime: string,
  rentalEndTime: string
): Promise<APIResult | undefined> => {
  const request: RentalTimeUpdateRequest = {
    rentalStartTime,
    rentalEndTime,
  };

  return runOrderMutation(
    () => GeneratedOrderService.putApiOrderUpdateRentalTimes(orderID, request),
    "租車時間更新成功",
    "租車時間更新失敗"
  );
};

export const transferOrder = async (
  orderID: string,
  targetUserUID: number
): Promise<APIResult | undefined> => {
  const request: TransferOrderRequest = {
    targetUserUID,
  };

  try {
    configureOrderApiClient();
    await callApi(
      () => GeneratedOrderService.putApiOrderTransferOrder(orderID, request),
      "轉移訂單失敗"
    );

    return {
      statusCode: 200,
      message: "訂單轉移成功",
      data: null,
    };
  } catch (error) {
    console.log(error);
    const failed = parseErrorResult(error, "轉移訂單失敗");

    if (failed.statusCode === 404 && !failed.message) {
      failed.message = "訂單或目標用戶不存在";
    } else if (failed.statusCode === 403 && !failed.message) {
      failed.message = "您無權轉移此訂單";
    } else if (failed.statusCode === 500 && !failed.message) {
      failed.message = "轉移訂單時發生錯誤";
    }

    return failed;
  }
};

export const updateOrderDetails = async (
  orderID: string,
  updates: {
    orderType?: string;
    address?: string;
    destinationAddress?: string | null;
    terminal?: string;
    rideTime?: string;
    flightNo?: string;
    flightTime?: string;
    email?: string;
    vehicleID?: string;
    luggages?: string;
    luggageNote?: string;
    passengerName?: string;
    passengerPhone?: string;
    passengerID?: string;
    passengerAdult?: number;
    passengerBaby?: number;
    rideFee?: number;
    driverCommission?: string;
    viaPoints?: string[];
    specialRequests?: string[];
    meetAndGreetDetails?: string | null;
    supplierId?: number | null;
    sourceChannel?: string | null;
    externalBookingNo?: string | null;
    chatApp?: string | null;
    chatId?: string | null;
    assignedCarModel?: string | null;
    isBlindOrder?: boolean;
  }
): Promise<APIResult | undefined> => {
  try {
    configureOrderApiClient();
    const request: UpdateOrderRequest & Record<string, unknown> = {
      ...updates,
      chatID: updates.chatId,
    };
    const data = await callApi(
      () => GeneratedOrderService.putApiOrderUpdateOrderDetails(orderID, request),
      "訂單更新失敗"
    );
    const response = data as { message?: string; order?: unknown };

    return {
      statusCode: 200,
      message: response?.message || "訂單更新成功",
      data: response?.order ?? null,
    };
  } catch (error) {
    console.log(error);
    return parseErrorResult(error, "訂單更新失敗");
  }
};

export const deleteOrder = async (orderID: string) => {
  try {
    configureOrderApiClient();
    const data = await callApi(
      () => GeneratedOrderService.deleteApiOrder(orderID),
      "刪除訂單失敗"
    );
    return { success: true, message: data };
  } catch (error) {
    console.log(error);
    if (error instanceof Error) {
      return { success: false, error: error.message };
    }
    return { success: false, error: String(error) };
  }
};
